/**
 * @deprecated Phase A.1 — Prefer importing from `design-studio/components`'s
 * new typography module:
 *
 *   import { H1, H2, H3, Body, Lead, Pretitle } from 'design-studio/components'
 *
 * `TextH1`/`TextH2`/`TextH3`/`TextH1Subtitle`/`TextH2Subtitle`/`TextPreTitle`/
 * `TextParagraph` remain wired up here while consuming sites migrate. See the
 * "Deprecated surface" table in `context/PUBLIC-EXPORTS.md` for the rename.
 *
 * Font loaders (`spaceGrotesk`, `poppins`, `ibmPlexMono`) re-export from
 * `../typography/fonts` so the same `next/font` instance is reused across the
 * app — no double download of Space Grotesk.
 */

// Utils
import { Join } from '../../lib/utils'

// Re-export fonts from the new module so call sites that already import them
// from `ui/typography` keep working without paying for a duplicate font load.
export { spaceGrotesk, poppins, ibmPlexMono } from '../typography/fonts'

interface TextH1Props {
  children: React.ReactNode
  fontFamily?: string // Optional fontFamily prop
  fontStyle?: string // Optional fontSize prop, defaults to 'text-5xl sm:text-6xl'
  colors?: string // Optional colors prop, defaults to 'text-indigo-950 dark:text-white'
  className?: string // Optional additional classes
}

export function TextH1({
  children,
  fontFamily,
  fontStyle = 'text-5xl sm:text-6xl font-semibold dark:font-normal pb-2',
  colors = 'text-indigo-950 dark:text-white',
  className,
}: TextH1Props) {
  const Classes = Join(
    '',
    'text-left',
    fontStyle,
    '',
    'tracking-tight',
    fontFamily,
    colors,
    className,
  )

  return <h1 className={Classes}>{children}</h1>
}

interface TextPreTitleProps {
  children: React.ReactNode
  colors?: string // Optional colors prop, defaults to 'text-blue-500'
  className?: string // Optional additional classes
}

export function TextPreTitle({
  children,
  colors = 'text-blue-500',
  className,
}: TextPreTitleProps) {
  const Classes = Join('text-sm font-semibold', colors, className)

  return <p className={Classes}>{children}</p>
}

interface TextH2Props {
  children: React.ReactNode
  fontFamily?: string // Optional fontFamily prop
  colors?: string // Optional colors prop, defaults to 'text-blue-900 dark:text-white'
  text?: string // Optional text prop, defaults to 'font-normal text-4xl sm:text-5xl leading-snug'
  className?: string // Optional additional classes
}

export function TextH2({
  children,
  fontFamily,
  text = 'font-normal text-4xl sm:text-5xl leading-snug',
  colors = 'text-slate-950 dark:text-white',
  className,
}: TextH2Props) {
  const Classes = Join(text, fontFamily, colors, className)

  return <h2 className={Classes}>{children}</h2>
}

interface TextH3Props {
  children: React.ReactNode
  fontFamily?: string // Optional fontFamily prop
  text?: string // Optional text prop, defaults to 'text-2xl sm:text-3xl leading-snug font-light'
  colors?: string // Optional colors prop, defaults to 'text-blue-900 dark:text-white'
  className?: string // Optional additional classes
}

export function TextH3({
  children,
  fontFamily,
  text = 'text-2xl sm:text-3xl leading-snug font-light',
  colors = 'text-slate-950 dark:text-white',
  className,
}: TextH3Props) {
  const Classes = Join(text, fontFamily, colors, className)

  return <h3 className={Classes}>{children}</h3>
}

interface TextH1SubtitleProps {
  children: React.ReactNode
  colors?: string // Optional colors prop, defaults to 'text-slate-700 dark:text-slate-300'
  className?: string // Optional additional classes
  fontFamily?: string // Optional fontFamily prop
}

export function TextH1Subtitle({
  children,
  colors = 'text-slate-700 dark:text-slate-300',
  className,
  fontFamily = 'font-mono',
}: TextH1SubtitleProps) {
  const Classes = Join(
    'mt-3',
    'text-left text-pretty',
    'text-2xl',
    colors,
    className,
    fontFamily,
  )

  return <p className={Classes}>{children}</p>
}

interface TextH2SubtitleProps {
  children: React.ReactNode
  colors?: string // Optional colors prop, defaults to 'text-slate-600 dark:text-slate-300'
  className?: string // Optional additional classes
}

export function TextH2Subtitle({
  children,
  colors = 'text-slate-600 dark:text-slate-300',
  className,
}: TextH2SubtitleProps) {
  const Classes = Join(
    'mx-auto',
    'mt-4',
    'text-xl',
    'font-mono',
    colors,
    className,
  )

  return <p className={Classes}>{children}</p>
}

interface TextParagraphProps {
  children: React.ReactNode
  colors?: string // Optional colors prop, defaults to 'text-slate-600 dark:text-slate-300'
  text?: string // Optional size prop, defaults to 'text-lg'
  className?: string // Optional additional classes
}

export function TextParagraph({
  children,
  colors = 'text-slate-600 dark:text-slate-300',
  text = 'text-base font-light leading-relaxed',
  className,
}: TextParagraphProps) {
  // `font-display` baseline ensures legacy call sites still resolve to
  // Space Grotesk via the `--font-display` CSS var even when the parent
  // context doesn't force a font class (modern consumers put Inter on
  // `<body>` via `font-sans`, which would otherwise cascade into here).
  const Classes = Join('font-display mt-3 lg:mt-4', text, colors, className)

  return <p className={Classes}>{children}</p>
}
